// Two sources of "that field is wrong", reduced to one shape.
//
// The client validates against the SAME generated schema the server validates
// against, before sending anything: the server is the authority, but a caller
// should not need a round trip to learn that a required name is empty. When the
// server does disagree — a reference that does not exist, a uniqueness clash, a
// rule that needs the stored row — it answers `crm-validation-failed`, and any
// field it names rides in `details`. Both land here, keyed by field name, so a
// form renders one thing either way.

import type { z } from "zod";
import { fieldErrorsOf, isApiProblem, type FieldError } from "../api/problem.js";

/** Field name -> the message to show under that input. */
export type FieldErrors = Readonly<Record<string, string>>;

/**
 * Zod issues, keyed by the field they name. The first issue per field wins:
 * stacking "required" under "too short" tells a reader nothing extra, and the
 * contract's refinements (the amount/rate XOR, the ordered date range) already
 * put their message on the field a person would go fix.
 */
export function zodFieldErrors(error: z.ZodError): FieldErrors {
  const errors: Record<string, string> = {};
  for (const issue of error.issues) {
    const field = issue.path.map(String).join(".");
    if (field === "") continue;
    errors[field] ??= issue.message;
  }
  return errors;
}

export interface ServerErrors {
  /** Errors naming a field this form actually renders. */
  fields: FieldErrors;
  /**
   * Errors naming something the form does NOT render — a derived column, a
   * field only the API knows about. Shown in the summary bar instead of being
   * dropped, because an invisible validation error is a form that refuses to
   * save for no stated reason.
   */
  unmapped: readonly FieldError[];
}

export function serverFieldErrors(error: unknown, known: readonly string[]): ServerErrors {
  if (!isApiProblem(error)) return { fields: {}, unmapped: [] };

  const fields: Record<string, string> = {};
  const unmapped: FieldError[] = [];
  const knownFields = new Set(known);

  for (const fieldError of fieldErrorsOf(error.problem)) {
    if (knownFields.has(fieldError.field)) {
      fields[fieldError.field] ??= fieldError.message;
    } else {
      unmapped.push(fieldError);
    }
  }

  return { fields, unmapped };
}
